/**
 * MSR Portal service — Workstream 4 Phase 1.
 *
 * Three RPC wrappers. All three RPCs derive the actor from
 * auth.uid() server-side; the frontend never passes a user/profile
 * id. Per auditor: don't trust a client-supplied identity for MSR
 * data access.
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type { EventDayRow } from "../utils/eventServiceDays";

export interface MsrScheduleRow {
  event_id: UUID;
  title: string | null;
  event_type: string | null;
  status: string | null;
  start_time: string | null;
  end_time: string | null;
  timezone: string | null;
  start_date: string | null;
  location_name: string | null;
  location_address: string | null;
  via_msr_id: boolean;
  via_cta: boolean;
  owned_cta_count: number;
  /** Migration 414: on the event's Sales Reps list (the source of truth for "assigned"). Absent before 414. */
  via_sales_reps?: boolean;
  sales_rep_role?: "lead" | "assigned" | null;
  /** The days a per-day rep works (YYYY-MM-DD, event-local); null = every day of the event. */
  sales_rep_days?: string[] | null;
}

export interface MsrEventCtaRow {
  cta_id: UUID;
  name: string | null;
  cta_type: string | null;
  base_url: string | null;
  parameters: Record<string, string> | null;
  generated_url: string | null;
  qr_code_url: string | null;
  is_system_generic: boolean;
  sales_position_id: UUID | null;
  /**
   * Migration 530 (MSR EVT-03). The event's account CTA (is_system_generic) is returned only to a caller who may
   * operate the event; these describe where it came from. Null on the rep's own CTAs. Optional so a client that
   * reaches a database without 530 still renders.
   */
  voucher_amount?: number | null;
  account_cta_status?:
    | "from_account"
    | "prepared_without_account_cta"
    | "deferred"
    | "not_prepared"
    | null;
  source_organization_name?: string | null;
  source_cta_name?: string | null;
  source_activity_type?: string | null;
  /**
   * Migration 530 v2 (EVT-04, owner 2026-10-12): on the event QR only. False = not ready (no affid / promo, or no QR
   * yet); the server then withholds its URL, QR and codes, and the page shows "not ready yet". Null on the rep's own
   * CTAs; undefined from a database without 530 v2 (treated as ready, today's behaviour).
   */
  qr_ready?: boolean | null;
}

export interface MsrEventAttendeeRow {
  attendee_id: UUID;
  contact_id: UUID | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  attendance_status: string | null;
  attendee_type: string | null;
  cta_id: UUID | null;
  cta_name: string | null;
  registration_date: string | null;
  is_zuora_confirmed: boolean;
}

// MSR Portal Phase 2 — per-rep Sales History (read-only). Both RPCs derive the
// actor server-side via current_sales_position_ids(); a non-rep gets zero rows.
export interface MsrSalesHistoryRow {
  source_type: "voucher" | "attendee";
  voucher_id: UUID | null;
  event_id: UUID;
  event_title: string | null;
  event_date: string | null;
  /** The event's LOCAL date (YYYY-MM-DD) in its own timezone -- the date the From/To filters use. */
  event_local_date?: string | null;
  event_timezone?: string | null;
  cta_id: UUID | null;
  cta_name: string | null;
  cta_type: string | null;
  contact_id: UUID;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  member_number: string | null;
  voucher_status: string | null;
  signup_at: string | null;
  is_zuora_confirmed: boolean;
  total_count: number;
}

export interface MsrSalesHistorySummary {
  total_signups: number;
  verified_count: number;
  pending_count: number;
  voucher_count: number;
  attendee_count: number;
  event_count: number;
}

export interface MsrSalesHistoryFilter {
  dateFrom?: string;
  dateTo?: string;
  eventType?: string;
  page?: number;
  limit?: number;
}

export async function getMsrSalesHistory(
  filter: MsrSalesHistoryFilter = {},
): Promise<{ rows: MsrSalesHistoryRow[]; total: number }> {
  const { data, error } = await supabase.rpc("get_msr_sales_history", {
    p_date_from: filter.dateFrom ?? null,
    p_date_to: filter.dateTo ?? null,
    p_event_type: filter.eventType ?? null,
    p_page: filter.page ?? 1,
    p_limit: filter.limit ?? 50,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as MsrSalesHistoryRow[];
  return { rows, total: rows.length > 0 ? Number(rows[0].total_count) : 0 };
}

export async function getMsrSalesHistorySummary(
  filter: MsrSalesHistoryFilter = {},
): Promise<MsrSalesHistorySummary> {
  const { data, error } = await supabase.rpc("get_msr_sales_history_summary", {
    p_date_from: filter.dateFrom ?? null,
    p_date_to: filter.dateTo ?? null,
    p_event_type: filter.eventType ?? null,
  });
  if (error) {
    throw error;
  }
  const row = (data as MsrSalesHistorySummary[] | null)?.[0];
  return (
    row ?? {
      total_signups: 0,
      verified_count: 0,
      pending_count: 0,
      voucher_count: 0,
      attendee_count: 0,
      event_count: 0,
    }
  );
}

export async function getMsrSchedule(
  includePast = false,
  windowDays = 90,
): Promise<MsrScheduleRow[]> {
  const { data, error } = await supabase.rpc("get_msr_schedule", {
    p_include_past: includePast,
    p_window_days: windowDays,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as MsrScheduleRow[];
}

export async function getMsrEventCtas(
  eventId: UUID,
): Promise<MsrEventCtaRow[]> {
  const { data, error } = await supabase.rpc("get_msr_event_ctas", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as MsrEventCtaRow[];
}

export async function getMsrEventAttendees(
  eventId: UUID,
): Promise<MsrEventAttendeeRow[]> {
  const { data, error } = await supabase.rpc("get_msr_event_attendees", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as MsrEventAttendeeRow[];
}

/**
 * The sales_position ids the logged-in user is assigned to (via
 * assigned_profile_id = auth.uid()). Used by the MSR Availability Calendar to
 * scope read + default the position on new availability blocks. Resolves
 * server-side; the frontend never supplies an identity.
 */
export async function getMyCurrentSalesPositionIds(): Promise<UUID[]> {
  const { data, error } = await supabase.rpc("current_sales_position_ids");
  if (error) {
    throw error;
  }
  if (!data) {
    return [];
  }
  // setof uuid → array of scalars, or array of { current_sales_position_ids }
  return (data as unknown[]).map((r) =>
    typeof r === "string"
      ? (r as UUID)
      : (r as { current_sales_position_ids: UUID }).current_sales_position_ids,
  );
}

/**
 * MSR Member Lookup — resolve a member by email OR member id (contacts.membership_id)
 * and return an allowlisted status + products snapshot from the CRM's cached copy
 * (contacts + contact_subscriptions). Backed by the SECURITY DEFINER RPC
 * msr_member_lookup, which fails closed on role (msr/admin/superadmin) — MSRs are
 * excluded from the contacts RLS so this cannot be a client-side select.
 */
export interface MsrMemberSubscription {
  status: string | null;
  product_names: string[] | null;
  subscription_number: string | null;
  subscription_type: string | null;
  region: string | null;
  term_start_date: string | null;
  term_end_date: string | null;
  auto_renew: boolean | null;
}

export interface MsrMemberLookupResult {
  found: boolean;
  contact_id?: UUID;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  membership_id?: string | null;
  contact_type?: string | null;
  member_status?: string | null;
  status?: string | null;
  membership_tier?: string | null;
  membership_type?: string | null;
  has_active_subscription?: boolean | null;
  is_usls_member?: boolean | null;
  is_primary_member?: boolean | null;
  paying_member?: boolean | null;
  product_names?: string[];
  member_start_date?: string | null;
  member_end_date?: string | null;
  member_renewal_date?: string | null;
  member_expiration_date?: string | null;
  subscriptions?: MsrMemberSubscription[];
}

export async function msrMemberLookup(
  query: string,
): Promise<MsrMemberLookupResult> {
  const { data, error } = await supabase.rpc("msr_member_lookup", {
    p_query: query,
  });
  if (error) {
    throw error;
  }
  return (data ?? { found: false }) as MsrMemberLookupResult;
}

/**
 * GS-03: the `event_days` rows of these events (only events in the MSR date model have any). Readable by any active
 * CRM profile (RLS event_days_read). Best effort: on any error the caller derives the days from each event's schedule,
 * so a failed read never hides an event.
 */
export async function getEventDaysFor(
  eventIds: UUID[],
  opts?: { throwOnError?: boolean },
): Promise<EventDayRow[]> {
  const ids = [...new Set(eventIds)].filter(Boolean);
  if (ids.length === 0) {
    return [];
  }
  const out: EventDayRow[] = [];
  // Bounded IN lists (URL length).
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase
      .from("event_days")
      .select("id, event_id, service_date, window_start, window_end")
      .in("event_id", ids.slice(i, i + 150));
    if (error) {
      if (opts?.throwOnError) {
        throw error;
      }
      console.warn("getEventDaysFor: falling back to derived days", error);
      return [];
    }
    out.push(...((data ?? []) as EventDayRow[]));
  }
  return out;
}

/**
 * MSR item 5: the rep's private calendar-feed (ICS) link. msr_calendar_feed_link (mig 562) acts on the CALLER only
 * (auth.uid(), active msr) -- there is no user parameter. 'get' never creates a link.
 */
export type MsrCalendarFeedAction = "get" | "create" | "rotate" | "revoke";

export interface MsrCalendarFeedLink {
  status: "none" | "active" | "unreadable";
  token?: string;
  created_at?: string;
  rotated_at?: string | null;
  last_fetched_at?: string | null;
}

export async function msrCalendarFeedLink(
  action: MsrCalendarFeedAction,
): Promise<MsrCalendarFeedLink> {
  const { data, error } = await supabase.rpc("msr_calendar_feed_link", {
    p_action: action,
  });
  if (error) {
    throw error;
  }
  return data as MsrCalendarFeedLink;
}

/** https URL of the feed (what Google "From URL" / Outlook "Subscribe from web" take). */
export function msrCalendarFeedUrl(token: string): string {
  const base = String(import.meta.env.VITE_SUPABASE_URL ?? "").replace(
    /\/+$/,
    "",
  );
  return `${base}/functions/v1/msr-calendar-feed/${token}.ics`;
}
